import { useEffect, useRef, useState } from "react";

import { Box } from "@chakra-ui/react";
import {
  IIndividualSavingsPayload,
  IIndividualSavingsResult,
  IIndividualSavingsStatsPayload,
  TSavingsSource,
  TSavingsStatus,
} from "api/individual-user-management/types";
import { useIndividuals } from "contexts/individuals-context";
import {
  individualTableColumnCss, // individualTypeCss,
} from "features/individuals/individuals/styles";
import { useSearchParams } from "react-router-dom";
import {
  capitalizeFirstLetter,
  formatDateToDDMMMYYYY,
  handleAmountFormat,
} from "utils";
import { ONE_NAIRA_IN_KOBO } from "utils/constants";

import Pagination from "ui/pagination";
import { IPaginationData } from "ui/pagination/types";
import { Spinner } from "ui/spinner";
import { Status } from "ui/status";
// import { activeTextToStatus } from "ui/status/const";
import { TStatus } from "ui/status/types";
// import { TStatus } from "ui/status/types";
import Table from "ui/table";
import TableWrapper from "ui/table/table-wrapper";
import { Heading3, Paragraph3 } from "ui/typography";

import { savingsColumns } from "../../../individuals/individuals-list-table/const";
import {
  DEFAULT_ACCOUNT_TRANSACTIONS_PAGINATION_PARAMS, // DEFAULT_ACCOUNT_Savings_PAGINATION_PARAMS,
  ZERO,
} from "../consts";
import { TableHeaderCTAs } from "./TableHeaderCTAs";
import { MoreQuickActions } from "./more-quick-actions";
import { ISavingsItem } from "./more-quick-actions/types";

export const getTransationStatus = (status: string) => {
  let Savingstatus = status;

  if (Savingstatus === "active") {
    Savingstatus = "success";
  } else if (Savingstatus === "deleted" || Savingstatus === "stopped") {
    Savingstatus = "error";
  } else if (Savingstatus === "paused") {
    Savingstatus = "purple";
  } else if (
    Savingstatus === "completed" ||
    Savingstatus === "disbursed" ||
    Savingstatus === "matured"
  ) {
    Savingstatus = "blue";
  } else {
    Savingstatus = "disabled";
  }

  return Savingstatus;
};

export const getTransationStatusText = (status: string) => {
  let Savingstatus = status;

  if (Savingstatus === "active") {
    Savingstatus = "Active";
  } else if (
    Savingstatus === "completed" ||
    Savingstatus === "disbursed" ||
    Savingstatus === "matured"
  ) {
    Savingstatus = "Completed";
  } else if (Savingstatus === "deleted" || Savingstatus === "stopped") {
    Savingstatus = "Closed";
  } else {
    Savingstatus = capitalizeFirstLetter(Savingstatus);
  }

  return Savingstatus;
};

const SelectedSavings = () => {
  // const { data } = props;
  // const { individualId: userId } = useParams();
  const {
    individualDetails,
    isLoadingIndividualSavings,
    individualSavingsResult,
    handleGetIndividualSavings,
    isLoadingIndividualSavingStats,
    individualSavingsResultStats,
    handleGetIndividualSavingsStats,
  } = useIndividuals();
  const [isRefreshing, setIsRefreshing] = useState(false);

  const walletId = individualDetails?.walletId;

  const [
    individualSavingsTransformedResult,
    setIndividualSavingsTransformedResult,
  ] = useState<ISavingsItem[] | null>(null);

  const tableRef = useRef<HTMLTableElement | null>(null);
  const [searchParams] = useSearchParams();
  const page = searchParams.get("page");
  const limit = searchParams.get("limit");
  const source = searchParams.get("source");
  const status = searchParams.get("status");
  const search = searchParams.get("search");
  const startDate = searchParams.get("startDate");
  const endDate = searchParams.get("endDate");
  const filterRetryCount = searchParams.get("filterRetryCount");

  const transformData = (data: IIndividualSavingsResult): ISavingsItem[] => {
    const items: ISavingsItem[] = data.goals.map((goal: any) => ({
      id: goal.id,
      walletId: goal.walletId,
      interest: goal.totalInterest,
      amountSaved: goal.totalAmountSaved,
      status: goal.status,
      startsAt: goal.startsAt,
      endsAt: goal.endsAt,
      createdAt: goal.createdAt,
      goalName: goal.goal,
      target: goal.target,
      type: "goal",
      source: goal.source,
    }));

    // Insert the flex item as the first element in the array
    if (data.flex && data.flex.flexInterest !== undefined) {
      items.unshift({
        id: data.flex.id,
        walletId: data.flex.walletId,
        interest: data.flex.flexInterest,
        amountSaved: data.flex.balance,
        status: data.flex.status,
        startsAt: data.flex.startsAt,
        endsAt: data.flex.endsAt,
        createdAt: data.flex.createdAt,
        goalName: "Flex Savings",
        target: null,
        type: "flex",
        source: data.flex.source,
      });
    }

    return items;
  };

  const totalBalance =
    (individualSavingsResultStats &&
      Number(individualSavingsResultStats.total)) ||
    ZERO;

  const [yieldEarnedNaira, yieldEarnedKobo] = handleAmountFormat({
    value: String(totalBalance / ONE_NAIRA_IN_KOBO),
    includeNairaSymbol: true,
    shouldFormatZero: true,
    isFormattedWithDecimal: true,
  }).split(".");

  useEffect(() => {
    if (!walletId) return;
    const statsPayload: IIndividualSavingsStatsPayload = {
      walletId,
    };
    void handleGetIndividualSavingsStats(statsPayload, {
      isRefetching: false,
    });
    const payload: IIndividualSavingsPayload = {
      walletId,
      page: String(page || DEFAULT_ACCOUNT_TRANSACTIONS_PAGINATION_PARAMS.page),
      limit: String(
        limit || DEFAULT_ACCOUNT_TRANSACTIONS_PAGINATION_PARAMS.limit
      ),
    };

    if (startDate) {
      payload["startDate"] = startDate;
    }

    if (endDate) {
      payload["endDate"] = endDate;
    }

    if (source) {
      const transactionSource = source.split(",");
      const filteredSource = transactionSource
        .filter((source) => source !== "all")
        .join(",");
      payload["source"] = filteredSource as TSavingsSource;
    }

    if (status) {
      const transactionStatus = status.split(",");
      const filteredStatus = transactionStatus
        .filter((transaction) => transaction !== "all")
        .join(",");
      payload["status"] = filteredStatus as TSavingsStatus;
    }
    if (search) {
      payload["search"] = search;
      payload["searchBy"] = "goal";
    }

    void handleGetIndividualSavings(payload, {
      isRefetching: false,
    });
  }, [
    walletId,
    page,
    limit,
    startDate,
    endDate,
    status,
    source,
    filterRetryCount,
  ]);

  const { flex, goals, ...paginationData } = individualSavingsResult || {};

  useEffect(() => {
    if (!individualSavingsResult) return;
    const transformedData = transformData({
      flex,
      goals,
      ...paginationData,
    } as IIndividualSavingsResult);
    setIndividualSavingsTransformedResult(transformedData);
  }, [individualSavingsResult]);

  const formattedData = individualSavingsTransformedResult?.map((saving) => {
    const {
      id,
      goalName,
      amountSaved,
      // walletId,
      status,
      // startsAt,
      createdAt,
      endsAt,
      type,
      target,
      interest,
      source,
    } = saving as ISavingsItem;

    return {
      id,
      Name: (
        <Box className="font-medium text-sm text-[#667085]">{goalName}</Box>
      ),
      "Savings Type": (
        <Box css={individualTableColumnCss}>
          {type === "flex" ? "Flex" : "Goal"}
        </Box>
      ),
      "Target Amount": (
        <Box css={individualTableColumnCss}>
          {target === null
            ? "N/A"
            : handleAmountFormat({
                value: String(target / ONE_NAIRA_IN_KOBO),
                includeNairaSymbol: true,
                shouldFormatZero: true,
              })}
        </Box>
      ),
      "Amount Saved": (
        <Box className="font-semibold text-sm text-[#1A202C]">
          {handleAmountFormat({
            value: String(amountSaved / ONE_NAIRA_IN_KOBO),
            includeNairaSymbol: true,
            shouldFormatZero: true,
          })}
        </Box>
      ),
      "Interest Earned": (
        <Box css={individualTableColumnCss}>
          {handleAmountFormat({
            value: String(interest / ONE_NAIRA_IN_KOBO),
            includeNairaSymbol: true,
            shouldFormatZero: true,
          })}
        </Box>
      ),
      "Start Date": (
        <Box css={individualTableColumnCss}>
          {createdAt ? formatDateToDDMMMYYYY(createdAt) : "N/A"}
        </Box>
      ),
      "End Date": (
        <Box css={individualTableColumnCss}>
          {endsAt ? formatDateToDDMMMYYYY(endsAt) : "N/A"}
        </Box>
      ),
      "Funding By": (
        <Box css={individualTableColumnCss}>
          {(source && (source === "wallet" ? "Account" : "Card")) || "N/A"}
        </Box>
      ),
      Status: (
        <Status
          status={getTransationStatus(status) as TStatus}
          text={getTransationStatusText(status)}
          className="w-fit"
        />
      ),
      "": <MoreQuickActions individualDetails={saving} />,
    };
  });

  return (
    <div>
      <Box className="flex flex-col gap-1 px-6 py-4 mb-2 rounded-[20px] bg-white justify-start">
        <Paragraph3
          lineHeight="14px"
          textColor="#718096"
          fontWeight="400"
        >
          Total Savings Balance
        </Paragraph3>
        {/* {formatAmountIntoDecimalAndNaira(TOTAL_SAVINGS_BALANCE)} */}
        {isLoadingIndividualSavingStats ? (
          <Spinner size="20px" />
        ) : (
          <Heading3
            lineHeight="20px"
            fontWeight="700"
            textColor="#1A202C"
          >
            {yieldEarnedNaira}.
            <span className="text-sm font-[700]">
              {yieldEarnedKobo || "00"}
            </span>
          </Heading3>
        )}
      </Box>
      <TableWrapper className="!p-0">
        <TableHeaderCTAs
          handleGetTransactions={handleGetIndividualSavings}
          isLoadingTransactions={isLoadingIndividualSavings}
          isRefreshing={isRefreshing}
          setIsRefreshing={setIsRefreshing}
          walletId={walletId as number}
        />
        <Table
          columns={savingsColumns}
          data={formattedData || []}
          tableRef={tableRef}
          isLoading={isLoadingIndividualSavings && !isRefreshing}
        />
        <Pagination
          paginationData={paginationData as IPaginationData}
          data={individualSavingsTransformedResult}
          defaultPaginationParams={
            DEFAULT_ACCOUNT_TRANSACTIONS_PAGINATION_PARAMS
          }
          tableRef={tableRef}
          isLoadingTable={isLoadingIndividualSavings}
        />
      </TableWrapper>
    </div>
  );
};

export default SelectedSavings;
